import { Controller } from "@hotwired/stimulus"
import { Spring, rubberband, prefersReducedMotion } from "../lib/spring"
import { detent, landed } from "../lib/feedback"

// Free-canvas editor for an organization's certificate design.
//
// The canvas is an iframe showing the server's own renderer output, and the
// draggable handles live in a transparent overlay above it. That way there is
// exactly one implementation of how a design turns into pixels — what the admin
// drags around here is literally the document the PDF is printed from.
//
// Dragging and resizing writes straight to the iframe's DOM for immediate
// feedback; anything that changes the markup (text, fonts, images, layer order)
// round-trips through the server and grafts the HTML it returns into the live
// frame, so the page never reloads while someone is working on it.
//
// Selection is a list. One layer gets handles and the full inspector; several
// move as one block, align to each other and share the arrange tools.

const PT_TO_PX = 96 / 72
const SNAP_TOLERANCE = 4 // pt — how close an edge has to be to catch
const SNAP_RELEASE = 10 // pt — how far past it you must pull to break free
const MIN_SIZE = 4 // pt
const HANDLES = ["nw", "n", "ne", "e", "se", "s", "sw", "w"]
const HISTORY_LIMIT = 50
const SAVE_DELAY = 300
const DRAG_THRESHOLD = 3 // px on screen before a press counts as a drag
const EDGE_KEEP = 12 // pt of a layer that must stay on the page
const HISTORY_COALESCE_MS = 600
const DUPLICATE_OFFSET = 12 // pt a duplicate is nudged from its original
const DOUBLE_PRESS_MS = 400 // two presses on one layer inside this open it for editing
const GEOMETRY_FIELDS = ["x", "y", "w", "h", "rotation"]
// Fields the frame can be brought up to date on without the server: geometry
// is written straight to the node, the rest the frame never draws at all.
const LOCAL_FIELDS = [...GEOMETRY_FIELDS, "name", "locked", "lock_aspect"]

export default class extends Controller {
  static targets = [
    "frame", "overlay", "stage", "viewport", "layers", "inspector", "empty", "status", "zoom", "year",
    "certificate", "count"
  ]

  static values = {
    updateUrl: String,
    previewUrl: String,
    imagesUrl: String,
    document: Object,
    lockVersion: Number,
    pageWidth: Number,
    pageHeight: Number,
    bindings: Object,
    fonts: Array
  }

  connect() {
    // Turbo caches a preview of the page while navigating away and Stimulus
    // connects to it too. Saving from that clone would race the live editor and
    // trip optimistic locking for no reason.
    if (document.documentElement.hasAttribute("data-turbo-preview")) return

    this.doc = this.normalise(this.documentValue)
    this.selectedIds = []
    this.scaleMode = "fit"
    this.scale = 1
    this.past = []
    this.future = []
    this.images = []

    // Seed the undo stack with the state as loaded, so the very first change is
    // reachable by undo.
    this.past.push(JSON.stringify(this.doc))

    this.guides = []
    this.dragFrame = null
    this.layerFrame = null

    this.populatePickers()
    this.renderLayers()
    this.refreshFrame()
    this.loadImages()

    this.onResize = () => this.layout()
    window.addEventListener("resize", this.onResize)
  }

  disconnect() {
    window.removeEventListener("resize", this.onResize)
    this.finishTextEdit()
    this.stopPointerTracking()
    this.stopLayerTracking()
    this.cancelSettle()
    if (this.layerSpring) this.layerSpring.stop()
    clearTimeout(this.coalesceTimer)

    // Navigating away inside the save delay would otherwise drop the last edit.
    if (this.saveTimer) {
      clearTimeout(this.saveTimer)
      this.save({ keepalive: true })
    }
  }

  get elements() {
    return this.doc.elements
  }

  // Selected layers, in the order they were picked.
  get selection() {
    return this.selectedIds.map((id) => this.elements.find((el) => el.id === id)).filter(Boolean)
  }

  // The layer the inspector edits: the one and only selected layer.
  get selected() {
    return this.selectedIds.length === 1 ? this.selection[0] : null
  }

  // Selected layers that can actually be moved.
  get movable() {
    return this.selection.filter((el) => !el.locked)
  }

  isSelected(id) {
    return this.selectedIds.includes(id)
  }

  normalise(raw) {
    const elements = raw && Array.isArray(raw.elements) ? raw.elements : []
    return { elements: elements.filter((el) => el && typeof el === "object") }
  }

  // --- canvas --------------------------------------------------------------

  // What the canvas renders against: a real certificate when one is picked,
  // otherwise the stand-in traveller for the chosen year.
  previewParams() {
    const params = {}
    if (this.hasCertificateTarget && this.certificateTarget.value) {
      params.certificate_id = this.certificateTarget.value
    } else if (this.hasYearTarget && this.yearTarget.value) {
      params.year = this.yearTarget.value
    }
    return params
  }

  async refreshFrame() {
    const url = new URL(this.previewUrlValue, window.location.origin)
    Object.entries(this.previewParams()).forEach(([key, value]) => url.searchParams.set(key, value))

    const response = await fetch(url, { headers: { Accept: "text/html" } })
    this.setFrameHtml(await response.text())
  }

  // The first paint loads the renderer's whole document, script and all. After
  // that, fresh HTML is grafted into the live frame — its styles and its page —
  // rather than reloaded into it: no white flash, no font re-fetch, and the
  // overlay and iframe never disagree about what exists.
  setFrameHtml(html) {
    const doc = this.frameTarget.contentDocument
    const current = doc && doc.querySelector(".page")
    const fresh = new DOMParser().parseFromString(html, "text/html")
    const page = fresh.querySelector(".page")

    if (!current || !page) {
      this.frameTarget.addEventListener("load", () => this.layout(), { once: true })
      this.frameTarget.srcdoc = html
      return
    }

    doc.querySelector("style").textContent = fresh.querySelector("style").textContent
    current.replaceWith(page)
    this.refit()
    this.refitWhenFontsReady()
  }

  layout() {
    const pageWidth = this.pageWidthValue * PT_TO_PX
    const pageHeight = this.pageHeightValue * PT_TO_PX
    const availableWidth = Math.max(200, this.viewportTarget.clientWidth - 48)
    const availableHeight = Math.max(200, this.viewportTarget.clientHeight - 48)

    this.scale = this.scaleMode === "fit"
      ? Math.min(1, availableWidth / pageWidth, availableHeight / pageHeight)
      : parseFloat(this.scaleMode)

    this.frameTarget.style.width = `${pageWidth}px`
    this.frameTarget.style.height = `${pageHeight}px`
    this.frameTarget.style.transform = `scale(${this.scale})`
    this.stageTarget.style.width = `${pageWidth * this.scale}px`
    this.stageTarget.style.height = `${pageHeight * this.scale}px`

    this.renderOverlay()
  }

  zoomChanged() {
    this.scaleMode = this.zoomTarget.value
    this.layout()
  }

  previewChanged() {
    // A real certificate brings its own year; the year picker only steers the
    // stand-in.
    if (this.hasYearTarget && this.hasCertificateTarget) this.yearTarget.disabled = Boolean(this.certificateTarget.value)
    this.refreshFrame()
  }

  // pt on the page -> px on screen
  get pxPerPt() {
    return this.scale * PT_TO_PX
  }

  renderOverlay() {
    this.overlayTarget.innerHTML = ""
    const single = this.selectedIds.length === 1

    this.elements.forEach((el) => {
      const box = document.createElement("div")
      box.dataset.id = el.id
      box.style.position = "absolute"
      box.style.cursor = el.locked ? "default" : "move"
      Object.assign(box.style, this.boxStyle(el))

      if (this.isSelected(el.id)) {
        box.style.outline = "1.5px solid #2563eb"
        if (single) HANDLES.forEach((handle) => box.appendChild(this.buildHandle(handle, el)))
      } else {
        box.style.outline = "1px dashed rgba(37, 99, 235, 0.3)"
      }

      this.overlayTarget.appendChild(box)
    })

    // Several layers move as one block, so show the block.
    if (this.selectedIds.length > 1) this.overlayTarget.appendChild(this.buildUnionBox())
  }

  boxStyle(el) {
    const s = this.pxPerPt
    return {
      left: `${this.num(el.x) * s}px`,
      top: `${this.num(el.y) * s}px`,
      width: `${this.num(el.w) * s}px`,
      height: `${this.num(el.h) * s}px`,
      transform: this.num(el.rotation) ? `rotate(${this.num(el.rotation)}deg)` : "",
      opacity: el.hidden ? "0.3" : "1"
    }
  }

  buildHandle(position, el) {
    const handle = document.createElement("div")
    handle.dataset.handle = position
    handle.style.position = "absolute"
    handle.style.width = "9px"
    handle.style.height = "9px"
    handle.style.background = "#ffffff"
    handle.style.border = "1.5px solid #2563eb"
    handle.style.borderRadius = "2px"
    handle.style.cursor = el.locked ? "default" : `${position}-resize`

    const edge = { nw: [0, 0], n: [50, 0], ne: [100, 0], e: [100, 50], se: [100, 100], s: [50, 100], sw: [0, 100], w: [0, 50] }[position]
    handle.style.left = `${edge[0]}%`
    handle.style.top = `${edge[1]}%`
    handle.style.marginLeft = "-4.5px"
    handle.style.marginTop = "-4.5px"

    return handle
  }

  buildUnionBox() {
    const box = document.createElement("div")
    box.dataset.union = "true"
    box.style.position = "absolute"
    box.style.pointerEvents = "none"
    box.style.outline = "1px dashed rgba(37, 99, 235, 0.6)"
    Object.assign(box.style, this.frameStyle(this.unionOf(this.selection)))
    return box
  }

  frameStyle(frame) {
    const s = this.pxPerPt
    return {
      left: `${frame.x * s}px`,
      top: `${frame.y * s}px`,
      width: `${frame.w * s}px`,
      height: `${frame.h * s}px`
    }
  }

  // The smallest box around a set of layers, in pt.
  unionOf(items) {
    const left = Math.min(...items.map((el) => this.num(el.x)))
    const top = Math.min(...items.map((el) => this.num(el.y)))
    const right = Math.max(...items.map((el) => this.num(el.x) + this.num(el.w)))
    const bottom = Math.max(...items.map((el) => this.num(el.y) + this.num(el.h)))
    return { x: left, y: top, w: right - left, h: bottom - top }
  }

  // --- pointer interaction -------------------------------------------------

  stagePointerDown(event) {
    if (event.button !== undefined && event.button !== 0) return

    // A press anywhere on the canvas ends an in-place edit; the browser's own
    // blur would be cancelled by the preventDefault below.
    this.finishTextEdit()

    const box = event.target.closest("[data-id]")
    const additive = event.shiftKey

    if (!box) {
      if (!additive) this.select([])
      this.beginMarquee(event)
      return
    }

    const el = this.elements.find((candidate) => candidate.id === box.dataset.id)
    if (!el) return

    if (additive) {
      this.toggleSelected(el.id)
      return
    }

    // Double-clicks are spotted here rather than via the browser's dblclick:
    // selecting on the first press rebuilds the overlay, and a dblclick whose
    // two halves hit different nodes never reaches the layer.
    const press = this.lastPress
    this.lastPress = { id: el.id, at: event.timeStamp }
    if (press && press.id === el.id && event.timeStamp - press.at < DOUBLE_PRESS_MS) {
      this.lastPress = null
      if (el.type === "text" && !el.locked) {
        // The press's own focus change would blur the editor the moment it opened.
        event.preventDefault()
        this.startTextEdit(el)
        return
      }
    }

    // Pressing a layer that is already part of the selection keeps the
    // selection, so a block can be grabbed by any of its members. A press
    // that turns out not to be a drag narrows the selection to that layer.
    const member = this.selectedIds.length > 1 && this.isSelected(el.id)
    if (!member) this.select(el.id)

    const handle = el.locked ? null : event.target.closest("[data-handle]")
    const items = handle ? [el] : this.movable
    if (items.length === 0) return

    // Grabbing a layer that is still springing back should take it over from
    // wherever it currently is, not wait for the animation to land.
    this.cancelSettle()

    const frame = this.unionOf(items)
    this.drag = {
      el,
      member,
      items: items.map((item) => ({ el: item, origin: { x: this.num(item.x), y: this.num(item.y), w: this.num(item.w), h: this.num(item.h) } })),
      handle: handle ? handle.dataset.handle : null,
      origin: frame,
      frame,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      clientX: event.clientX,
      clientY: event.clientY,
      moved: false,
      shiftKey: event.shiftKey,
      altKey: event.altKey
    }
    this.held = { x: null, y: null }

    event.preventDefault()
    this.trackPointer(event)
  }

  // Capturing keeps the gesture alive when the pointer leaves the overlay, and
  // means a gesture the browser takes away arrives as pointercancel rather
  // than leaving it wedged open.
  trackPointer(event) {
    this.overlayTarget.setPointerCapture(event.pointerId)
    this.onPointerMove = (moveEvent) => this.pointerMove(moveEvent)
    this.onPointerUp = () => this.pointerUp()
    this.onPointerCancel = () => this.pointerCancel()
    this.overlayTarget.addEventListener("pointermove", this.onPointerMove)
    this.overlayTarget.addEventListener("pointerup", this.onPointerUp)
    this.overlayTarget.addEventListener("pointercancel", this.onPointerCancel)
  }

  // Pointers report faster than the screen redraws, so record the latest
  // position and do the work once per frame.
  pointerMove(event) {
    const gesture = this.drag || this.marquee
    if (!gesture) return

    gesture.clientX = event.clientX
    gesture.clientY = event.clientY
    gesture.shiftKey = event.shiftKey
    gesture.altKey = event.altKey

    if (this.dragFrame == null) {
      this.dragFrame = requestAnimationFrame(() => (this.drag ? this.applyDragFrame() : this.drawMarquee()))
    }
  }

  applyDragFrame() {
    this.dragFrame = null
    const drag = this.drag
    if (!drag) return

    const { el, handle, origin } = drag
    const screenDx = drag.clientX - drag.startX
    const screenDy = drag.clientY - drag.startY

    // A click wobbles a pixel or two on the way down. Without a threshold that
    // reads as a drag and writes an undo entry nobody asked for.
    if (!drag.moved) {
      if (Math.hypot(screenDx, screenDy) < DRAG_THRESHOLD) return
      drag.moved = true
    }

    const dx = screenDx / this.pxPerPt
    const dy = screenDy / this.pxPerPt

    if (handle) {
      const locked = this.aspectLocked(el)
      this.applyResize(el, handle, origin, dx, dy, drag.shiftKey ? !locked : locked)
      this.guides = []
      this.syncFrameElement(el)
      this.updateDragVisuals(drag)
      return
    }

    // The block moves as one: snapping and the page edge see its outline, and
    // every member follows by the same distance.
    const raw = { x: origin.x + dx, y: origin.y + dy }
    let frame = raw

    if (drag.altKey) {
      this.guides = []
      this.held = { x: null, y: null }
    } else {
      frame = this.applySnap(raw, origin, drag.items.map((item) => item.el.id))
    }

    this.placeItems(drag, this.resistFrame(frame, origin))
    this.updateDragVisuals(drag)
  }

  placeItems(drag, frame) {
    const dx = frame.x - drag.origin.x
    const dy = frame.y - drag.origin.y

    drag.items.forEach(({ el, origin }) => {
      el.x = this.round(origin.x + dx)
      el.y = this.round(origin.y + dy)
      this.syncFrameElement(el)
    })
    drag.frame = { ...frame }
  }

  pointerUp() {
    const drag = this.drag
    const marquee = this.marquee
    this.stopPointerTracking()

    if (marquee) {
      this.finishMarquee(marquee)
      return
    }
    if (!drag) return

    this.guides = []

    if (!drag.moved) {
      if (drag.member) this.select(drag.el.id)
      return
    }

    this.settleIntoBounds(drag, () => {
      this.pushHistory()
      this.refit()
      this.renderOverlay()
      this.fillInspector()
      this.scheduleSave({ refresh: false })
    })
  }

  // A gesture the browser took back — a context menu, a system swipe — should
  // leave the layers where they started rather than half-moved.
  pointerCancel() {
    const drag = this.drag
    const marquee = this.marquee
    this.stopPointerTracking()

    if (marquee) {
      if (marquee.node) marquee.node.remove()
      return
    }
    if (!drag) return

    drag.items.forEach(({ el, origin }) => {
      Object.assign(el, origin)
      this.syncFrameElement(el)
    })
    this.guides = []
    this.renderOverlay()
    this.fillInspector()
  }

  stopPointerTracking() {
    if (this.dragFrame != null) cancelAnimationFrame(this.dragFrame)
    this.dragFrame = null

    const gesture = this.drag || this.marquee
    if (gesture && this.hasOverlayTarget) {
      const { pointerId } = gesture
      if (pointerId !== undefined && this.overlayTarget.hasPointerCapture(pointerId)) {
        this.overlayTarget.releasePointerCapture(pointerId)
      }
      if (this.onPointerMove) this.overlayTarget.removeEventListener("pointermove", this.onPointerMove)
      if (this.onPointerUp) this.overlayTarget.removeEventListener("pointerup", this.onPointerUp)
      if (this.onPointerCancel) this.overlayTarget.removeEventListener("pointercancel", this.onPointerCancel)
    }

    this.onPointerMove = null
    this.onPointerUp = null
    this.onPointerCancel = null
    this.drag = null
    this.marquee = null
  }

  // --- marquee selection -----------------------------------------------------

  // Dragging on empty canvas sweeps out a rectangle and selects whatever it
  // touches. Locked and hidden layers are left alone — a full-page background
  // would otherwise ride along with every sweep.
  beginMarquee(event) {
    this.marquee = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      clientX: event.clientX,
      clientY: event.clientY,
      additive: event.shiftKey,
      base: [...this.selectedIds],
      node: null
    }

    event.preventDefault()
    this.trackPointer(event)
  }

  drawMarquee() {
    this.dragFrame = null
    const marquee = this.marquee
    if (!marquee) return

    if (!marquee.node) {
      if (Math.hypot(marquee.clientX - marquee.startX, marquee.clientY - marquee.startY) < DRAG_THRESHOLD) return

      marquee.node = document.createElement("div")
      marquee.node.dataset.marquee = "true"
      marquee.node.style.position = "absolute"
      marquee.node.style.pointerEvents = "none"
      marquee.node.style.border = "1px solid rgba(37, 99, 235, 0.8)"
      marquee.node.style.background = "rgba(37, 99, 235, 0.08)"
      this.overlayTarget.appendChild(marquee.node)
    }

    const rect = this.marqueeRect(marquee)
    marquee.node.style.left = `${rect.left * this.pxPerPt}px`
    marquee.node.style.top = `${rect.top * this.pxPerPt}px`
    marquee.node.style.width = `${(rect.right - rect.left) * this.pxPerPt}px`
    marquee.node.style.height = `${(rect.bottom - rect.top) * this.pxPerPt}px`

    // Show what a release would select as the sweep goes.
    const hits = new Set(this.hitsWithin(rect).map((el) => el.id))
    this.overlayTarget.querySelectorAll("[data-id]").forEach((box) => {
      const on = hits.has(box.dataset.id) || this.isSelected(box.dataset.id)
      box.style.outline = on ? "1.5px solid #2563eb" : "1px dashed rgba(37, 99, 235, 0.3)"
    })
  }

  // The swept rectangle in page pt.
  marqueeRect(marquee) {
    const origin = this.overlayTarget.getBoundingClientRect()
    const x1 = (marquee.startX - origin.left) / this.pxPerPt
    const y1 = (marquee.startY - origin.top) / this.pxPerPt
    const x2 = (marquee.clientX - origin.left) / this.pxPerPt
    const y2 = (marquee.clientY - origin.top) / this.pxPerPt
    return { left: Math.min(x1, x2), top: Math.min(y1, y2), right: Math.max(x1, x2), bottom: Math.max(y1, y2) }
  }

  hitsWithin(rect) {
    return this.elements.filter((el) => {
      if (el.locked || el.hidden) return false
      const x = this.num(el.x)
      const y = this.num(el.y)
      return x < rect.right && x + this.num(el.w) > rect.left && y < rect.bottom && y + this.num(el.h) > rect.top
    })
  }

  finishMarquee(marquee) {
    if (!marquee.node) return

    marquee.node.remove()
    const hits = this.hitsWithin(this.marqueeRect(marquee)).map((el) => el.id)
    this.select(marquee.additive ? [...new Set([...marquee.base, ...hits])] : hits)
  }

  // --- page boundary ---------------------------------------------------------

  // A sliver of the block has to stay on the page, otherwise it can never be
  // grabbed back.
  boundsFor(frame) {
    return {
      minX: -frame.w + EDGE_KEEP,
      maxX: this.pageWidthValue - EDGE_KEEP,
      minY: -frame.h + EDGE_KEEP,
      maxY: this.pageHeightValue - EDGE_KEEP
    }
  }

  resistFrame(frame, size) {
    const bounds = this.boundsFor(size)
    return {
      x: this.resist(frame.x, bounds.minX, bounds.maxX, this.pageWidthValue),
      y: this.resist(frame.y, bounds.minY, bounds.maxY, this.pageHeightValue)
    }
  }

  resist(value, min, max, dimension) {
    if (value < min) return min + rubberband(value - min, dimension)
    if (value > max) return max + rubberband(value - max, dimension)
    return value
  }

  // Released past the edge, the block springs home rather than stopping dead
  // wherever the finger left it.
  settleIntoBounds(drag, done) {
    const bounds = this.boundsFor(drag.origin)
    const current = drag.frame
    const x = Math.min(Math.max(current.x, bounds.minX), bounds.maxX)
    const y = Math.min(Math.max(current.y, bounds.minY), bounds.maxY)

    if ((x === current.x && y === current.y) || prefersReducedMotion()) {
      this.placeItems(drag, { x, y })
      done()
      return
    }

    // Each axis gets its own spring; one spring driving a 2D distance goes out
    // of step the moment the two axes are travelling at different speeds.
    let atRest = 0
    const settled = () => {
      atRest += 1
      if (atRest < 2) return
      this.settle = null
      done()
    }

    const draw = () => {
      this.placeItems(drag, drag.frame)
      this.updateDragVisuals(drag)
    }

    this.settle = {
      x: new Spring({
        value: current.x, response: 0.35, damping: 1,
        onUpdate: (value) => { drag.frame.x = value; draw() },
        onRest: settled
      }),
      y: new Spring({
        value: current.y, response: 0.35, damping: 1,
        onUpdate: (value) => { drag.frame.y = value; draw() },
        onRest: settled
      })
    }

    this.settle.x.to(x)
    this.settle.y.to(y)
  }

  cancelSettle() {
    if (!this.settle) return
    this.settle.x.stop()
    this.settle.y.stop()
    this.settle = null
  }

  // --- drag feedback ---------------------------------------------------------

  // Rebuilding the whole overlay per frame recreates every box and handle and
  // costs more than the drag itself; only what moved needs touching.
  updateDragVisuals(drag) {
    drag.items.forEach(({ el }) => {
      const box = this.overlayTarget.querySelector(`[data-id="${el.id}"]`)
      if (box) Object.assign(box.style, this.boxStyle(el))
    })

    const union = this.overlayTarget.querySelector("[data-union]")
    if (union) Object.assign(union.style, this.frameStyle(this.unionOf(this.selection)))

    this.renderGuides()
    if (drag.items.length === 1) this.updateGeometryFields(drag.items[0].el)
  }

  updateGeometryFields(el) {
    if (!this.hasInspectorTarget || this.inspectorTarget.classList.contains("hidden")) return

    ;["x", "y", "w", "h"].forEach((field) => {
      const input = this.inspectorTarget.querySelector(`[data-field="${field}"]`)
      if (input && document.activeElement !== input) input.value = this.num(el[field])
    })
  }

  // A snap nobody can see is indistinguishable from luck. Draw the edge the
  // layer landed on for as long as it holds.
  renderGuides() {
    Array.from(this.overlayTarget.querySelectorAll("[data-guide]")).forEach((node) => node.remove())

    ;(this.guides || []).forEach(({ axis, at }) => {
      const line = document.createElement("div")
      line.dataset.guide = axis
      line.style.position = "absolute"
      line.style.background = "#f43f5e"
      line.style.pointerEvents = "none"

      if (axis === "x") {
        line.style.left = `${at * this.pxPerPt}px`
        line.style.top = "0"
        line.style.width = "1px"
        line.style.height = "100%"
      } else {
        line.style.top = `${at * this.pxPerPt}px`
        line.style.left = "0"
        line.style.height = "1px"
        line.style.width = "100%"
      }

      this.overlayTarget.appendChild(line)
    })
  }

  applyResize(el, handle, origin, dx, dy, constrain) {
    let w = origin.w
    let h = origin.h

    if (handle.includes("e")) w = origin.w + dx
    if (handle.includes("w")) w = origin.w - dx
    if (handle.includes("s")) h = origin.h + dy
    if (handle.includes("n")) h = origin.h - dy

    const corner = handle.length === 2

    if (constrain && origin.w > 0 && origin.h > 0) {
      const ratio = origin.w / origin.h

      if (corner) {
        // Follow whichever axis the pointer travelled further along, measured
        // against the shape's own proportions so a wide box isn't dominated by
        // its long side.
        if (Math.abs(dx) * origin.h >= Math.abs(dy) * origin.w) h = w / ratio
        else w = h * ratio
      } else if (handle === "e" || handle === "w") {
        h = w / ratio
      } else {
        w = h * ratio
      }
    }

    w = Math.max(MIN_SIZE, w)
    h = Math.max(MIN_SIZE, h)

    // Edges the handle isn't dragging stay put.
    let x = handle.includes("w") ? origin.x + origin.w - w : origin.x
    let y = handle.includes("n") ? origin.y + origin.h - h : origin.y

    // A constrained edge drag also changes the axis it wasn't driving, so grow
    // that one evenly either side rather than pinning it to one edge.
    if (constrain && !corner) {
      if (handle === "e" || handle === "w") y = origin.y + ((origin.h - h) / 2)
      else x = origin.x + ((origin.w - w) / 2)
    }

    el.x = this.round(x)
    el.y = this.round(y)
    el.w = this.round(w)
    el.h = this.round(h)
  }

  // Graphics keep their proportions unless the admin says otherwise; other
  // shapes are free-form unless they opt in. Shift inverts whichever applies,
  // the way every other design tool behaves.
  aspectLocked(el) {
    if (el.lock_aspect === undefined || el.lock_aspect === null) return el.type === "image" || el.type === "qr"

    return Boolean(el.lock_aspect)
  }

  // Snaps the moving block's edges and centre to the page and to the layers
  // outside it, so things line up without pixel-hunting. Returns the snapped
  // position and records the guides it caught so the drag can draw them.
  applySnap(raw, size, excludedIds) {
    const vertical = [0, this.pageWidthValue / 2, this.pageWidthValue]
    const horizontal = [0, this.pageHeightValue / 2, this.pageHeightValue]

    this.elements.forEach((other) => {
      if (excludedIds.includes(other.id)) return
      vertical.push(this.num(other.x), this.num(other.x) + this.num(other.w) / 2, this.num(other.x) + this.num(other.w))
      horizontal.push(this.num(other.y), this.num(other.y) + this.num(other.h) / 2, this.num(other.y) + this.num(other.h))
    })

    const matched = []
    const frame = { x: raw.x, y: raw.y }

    const x = this.snapAxisHeld("x", raw.x, size.w, vertical)
    if (x) {
      frame.x = x.value
      matched.push({ axis: "x", at: x.guide })
    }

    const y = this.snapAxisHeld("y", raw.y, size.h, horizontal)
    if (y) {
      frame.y = y.value
      matched.push({ axis: "y", at: y.guide })
    }

    this.guides = matched
    return frame
  }

  // An edge that catches at the same distance it lets go at chatters: one pixel
  // of hand shake and the layer flickers on and off the guide. Once caught, the
  // edge holds until the pointer is pulled decisively clear of it — the same
  // detent a physical control has, and the moment worth a haptic tick.
  snapAxisHeld(axis, raw, size, guides) {
    const held = this.held ? this.held[axis] : null

    if (held !== null) {
      const offsets = [ 0, size / 2, size ]
      const nearest = offsets
        .map((offset) => ({ offset, distance: Math.abs(raw + offset - held) }))
        .sort((a, b) => a.distance - b.distance)[0]

      if (nearest.distance <= SNAP_RELEASE) {
        return { value: held - nearest.offset, guide: held }
      }

      this.held[axis] = null
    }

    const best = this.snapAxis(raw, size, guides)
    if (!best) return null

    if (this.held) this.held[axis] = best.guide
    detent()

    return best
  }

  // Tries the block's leading edge, centre and trailing edge against every
  // guide and returns the new origin for the closest match inside tolerance.
  snapAxis(origin, size, guides) {
    let best = null

    ;[0, size / 2, size].forEach((offset) => {
      guides.forEach((guide) => {
        const distance = Math.abs(origin + offset - guide)
        if (distance <= SNAP_TOLERANCE && (best === null || distance < best.distance)) {
          best = { distance, value: guide - offset, guide }
        }
      })
    })

    return best
  }

  syncFrameElement(el) {
    const node = this.frameNode(el.id)
    if (!node) return

    node.style.left = `${this.num(el.x)}pt`
    node.style.top = `${this.num(el.y)}pt`
    node.style.width = `${this.num(el.w)}pt`
    node.style.height = `${this.num(el.h)}pt`

    const degrees = this.num(el.rotation)
    node.style.transform = degrees ? `rotate(${degrees}deg)` : ""
    node.style.transformOrigin = degrees ? "center center" : ""
  }

  frameNode(id) {
    const doc = this.frameTarget.contentDocument
    return doc ? doc.getElementById(id) : null
  }

  refit() {
    const frame = this.frameTarget.contentWindow
    if (frame && typeof frame.certificateFit === "function") frame.certificateFit()
  }

  // A face the design has only just started using arrives after the graft, and
  // text measured against the fallback face shrinks to the wrong size. Forcing
  // layout kicks the load off; the fit runs again once it lands.
  refitWhenFontsReady() {
    const doc = this.frameTarget.contentDocument
    if (!doc || !doc.fonts) return

    void doc.body.offsetWidth
    doc.fonts.ready.then(() => this.refit())
  }

  // --- arrange ---------------------------------------------------------------

  // One layer aligns to the page; several align to the block they make.
  align(event) {
    const mode = event.currentTarget.dataset.mode
    const items = this.movable
    if (items.length === 0) return

    const ref = items.length === 1
      ? { x: 0, y: 0, w: this.pageWidthValue, h: this.pageHeightValue }
      : this.unionOf(items)

    items.forEach((el) => {
      const w = this.num(el.w)
      const h = this.num(el.h)
      switch (mode) {
        case "left": el.x = ref.x; break
        case "center": el.x = ref.x + ((ref.w - w) / 2); break
        case "right": el.x = ref.x + ref.w - w; break
        case "top": el.y = ref.y; break
        case "middle": el.y = ref.y + ((ref.h - h) / 2); break
        case "bottom": el.y = ref.y + ref.h - h; break
      }
      el.x = this.round(el.x)
      el.y = this.round(el.y)
      this.syncFrameElement(el)
    })

    this.arranged()
  }

  // Even gaps between three or more layers, the outermost two staying put.
  distribute(event) {
    const axis = event.currentTarget.dataset.axis
    const size = axis === "x" ? "w" : "h"
    const items = [...this.movable].sort((a, b) => this.num(a[axis]) - this.num(b[axis]))
    if (items.length < 3) return

    const first = items[0]
    const last = items[items.length - 1]
    const span = this.num(last[axis]) + this.num(last[size]) - this.num(first[axis])
    const filled = items.reduce((sum, el) => sum + this.num(el[size]), 0)
    const gap = (span - filled) / (items.length - 1)

    let cursor = this.num(first[axis])
    items.forEach((el) => {
      el[axis] = this.round(cursor)
      cursor += this.num(el[size]) + gap
      this.syncFrameElement(el)
    })

    this.arranged()
  }

  arranged() {
    this.pushHistory()
    this.refit()
    this.renderOverlay()
    this.fillInspector()
    this.scheduleSave({ refresh: false })
  }

  // --- in-place text editing -----------------------------------------------

  // A textarea laid over the layer, dressed in the type the frame is already
  // rendering it in, so the copy is edited where it sits. It shows the source —
  // {{tags}} and all — since that is what is being written.
  startTextEdit(el) {
    this.finishTextEdit()

    const node = this.frameNode(el.id)
    if (!node) return

    const frameWindow = this.frameTarget.contentWindow
    const style = frameWindow.getComputedStyle(node)
    const inner = node.querySelector(".el-text-inner")
    const px = (value) => `${parseFloat(value) * this.scale}px`

    const editor = document.createElement("textarea")
    editor.dataset.textEditor = "true"
    editor.value = el.content || ""
    Object.assign(editor.style, this.boxStyle(el), {
      position: "absolute",
      zIndex: "5",
      margin: "0",
      padding: "0",
      border: "1.5px solid #2563eb",
      outline: "none",
      background: "rgba(255, 255, 255, 0.94)",
      resize: "none",
      overflow: "hidden",
      boxSizing: "border-box",
      whiteSpace: "pre-wrap",
      opacity: "1",
      fontFamily: style.fontFamily,
      fontSize: px(style.fontSize),
      fontWeight: style.fontWeight,
      fontStyle: style.fontStyle,
      lineHeight: style.lineHeight === "normal" ? "normal" : px(style.lineHeight),
      letterSpacing: style.letterSpacing === "normal" ? "normal" : px(style.letterSpacing),
      textTransform: style.textTransform,
      color: style.color,
      textAlign: inner ? frameWindow.getComputedStyle(inner).textAlign : "center"
    })

    if (inner) inner.style.visibility = "hidden"
    this.stageTarget.appendChild(editor)
    this.textEdit = { el, editor, inner, original: editor.value }

    editor.addEventListener("blur", () => this.finishTextEdit())
    editor.addEventListener("keydown", (keyEvent) => {
      if (keyEvent.key === "Escape" || (keyEvent.key === "Enter" && (keyEvent.metaKey || keyEvent.ctrlKey))) {
        keyEvent.preventDefault()
        this.finishTextEdit()
      }
    })

    editor.focus()
    editor.select()
  }

  finishTextEdit() {
    const edit = this.textEdit
    if (!edit) return

    this.textEdit = null
    const value = edit.editor.value
    edit.editor.remove()
    if (edit.inner) edit.inner.style.visibility = ""
    if (value === edit.original) return

    edit.el.content = value
    const field = this.inspectorTarget.querySelector('[data-group="text"] [data-field="content"]')
    if (field) field.value = value

    this.pushHistory()
    this.scheduleSave()
  }

  // --- layers --------------------------------------------------------------

  renderLayers() {
    this.layersTarget.innerHTML = ""

    // Topmost first, matching what the eye sees on the page.
    ;[...this.elements].reverse().forEach((el, reverseIndex) => {
      const index = this.elements.length - 1 - reverseIndex
      const item = document.createElement("li")
      item.dataset.layerId = el.id
      item.className = `designer-layer flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs select-none ${
        this.isSelected(el.id) ? "bg-blue-50 text-blue-900" : "text-gray-700 hover:bg-gray-50"
      }`
      item.addEventListener("click", (event) => {
        if (this.layerDragged) return
        if (event.shiftKey || event.metaKey || event.ctrlKey) this.toggleSelected(el.id)
        else this.select(el.id)
      })
      // A mouse has nowhere else for a vertical drag to go, so the whole row is
      // grabbable. Touch keeps the row free for scrolling and reorders from the
      // grip instead.
      item.addEventListener("pointerdown", (event) => {
        if (event.pointerType !== "mouse") return
        this.layerPointerDown(event, reverseIndex)
      })

      const grip = document.createElement("span")
      grip.dataset.grip = "true"
      grip.textContent = "⠿"
      grip.title = "Drag to reorder"
      grip.className = "px-0.5 text-gray-300 hover:text-gray-500 cursor-grab"
      grip.style.touchAction = "none"
      grip.addEventListener("pointerdown", (event) => {
        event.stopPropagation()
        this.layerPointerDown(event, reverseIndex)
      })
      item.appendChild(grip)

      const label = document.createElement("span")
      label.className = "flex-1 truncate"
      label.textContent = el.name || el.type
      if (el.hidden) label.style.opacity = "0.4"
      item.appendChild(label)

      item.appendChild(this.layerButton(el.hidden ? "◌" : "●", "Show or hide", () => {
        el.hidden = !el.hidden
        this.commit()
      }))
      item.appendChild(this.layerButton("↑", "Bring forward", () => this.reorder(index, index + 1)))
      item.appendChild(this.layerButton("↓", "Send backward", () => this.reorder(index, index - 1)))

      this.layersTarget.appendChild(item)
    })
  }

  layerButton(glyph, title, action) {
    const button = document.createElement("button")
    button.type = "button"
    button.title = title
    button.textContent = glyph
    button.className = "px-1 text-gray-400 hover:text-gray-900"
    button.addEventListener("click", (event) => {
      event.stopPropagation()
      action()
    })
    return button
  }

  reorder(from, to) {
    if (to < 0 || to >= this.elements.length) return

    const [element] = this.elements.splice(from, 1)
    this.elements.splice(to, 0, element)
    this.commit()
  }

  // --- layer reorder -------------------------------------------------------
  //
  // Rows follow the pointer directly rather than going through HTML5 drag and
  // drop. The browser's version swaps the row for a ghost image, can't be
  // grabbed back mid-flight, and never fires on touch at all.
  //
  // Indexes here are the panel's own top-first order, the reverse of the
  // document's back-to-front stacking.

  layerPointerDown(event, index) {
    if (event.button !== undefined && event.button !== 0) return
    if (event.target.closest("button")) return

    const rows = Array.from(this.layersTarget.children)
    const node = rows[index]
    if (!node || rows.length < 2) return

    if (this.layerSpring) this.layerSpring.stop()
    event.preventDefault()
    node.setPointerCapture(event.pointerId)

    this.layerDrag = {
      node,
      rows,
      index,
      to: index,
      pointerId: event.pointerId,
      startY: event.clientY,
      lastY: event.clientY,
      lastTime: event.timeStamp,
      velocity: 0,
      offset: 0,
      started: false,
      rects: rows.map((row) => row.getBoundingClientRect()),
      height: node.getBoundingClientRect().height
    }
    this.layerPointerY = event.clientY

    this.onLayerMove = (moveEvent) => this.layerPointerMove(moveEvent)
    this.onLayerUp = () => this.layerPointerUp()
    this.onLayerCancel = () => this.layerPointerCancel()
    node.addEventListener("pointermove", this.onLayerMove)
    node.addEventListener("pointerup", this.onLayerUp)
    node.addEventListener("pointercancel", this.onLayerCancel)
  }

  layerPointerMove(event) {
    const drag = this.layerDrag
    if (!drag) return

    // Keep a running velocity so the release can continue at the speed the
    // finger was actually moving instead of stopping dead.
    const elapsed = event.timeStamp - drag.lastTime
    if (elapsed > 0) drag.velocity = ((event.clientY - drag.lastY) / elapsed) * 1000
    drag.lastTime = event.timeStamp
    drag.lastY = event.clientY

    this.layerPointerY = event.clientY
    drag.offset = event.clientY - drag.startY

    if (!drag.started) {
      if (Math.abs(drag.offset) < DRAG_THRESHOLD) return
      this.startLayerDrag()
    }

    if (this.layerFrame == null) this.layerFrame = requestAnimationFrame(() => this.drawLayerDrag())
  }

  startLayerDrag() {
    const { node } = this.layerDrag
    this.layerDrag.started = true
    this.layerDragged = true

    node.dataset.dragging = "true"
    node.style.position = "relative"
    node.style.zIndex = "10"
    node.style.transition = "none"
    node.style.cursor = "grabbing"
    node.style.boxShadow = "0 8px 20px rgba(31, 42, 31, 0.18)"
  }

  drawLayerDrag() {
    this.layerFrame = null
    const drag = this.layerDrag
    if (!drag || !drag.started) return

    const { node, rows, rects, index, height } = drag
    node.style.transform = `translate3d(0, ${drag.offset}px, 0)`

    // Where the row lands if released now: the furthest slot whose midpoint the
    // pointer has crossed.
    let to = index
    rects.forEach((rect, i) => {
      const middle = rect.top + (rect.height / 2)
      if (i < index && this.layerPointerY < middle) to = Math.min(to, i)
      if (i > index && this.layerPointerY > middle) to = Math.max(to, i)
    })
    drag.to = to

    // The rows between the old and new slot step aside to open the gap, so the
    // outcome is visible before the drop rather than announced by a hairline.
    rows.forEach((row, i) => {
      if (i === index) return

      let shift = 0
      if (to > index && i > index && i <= to) shift = -height
      if (to < index && i < index && i >= to) shift = height
      row.style.transform = shift ? `translate3d(0, ${shift}px, 0)` : ""
    })
  }

  layerPointerUp() {
    const drag = this.layerDrag
    if (!drag) return

    if (!drag.started) {
      this.stopLayerTracking()
      return
    }

    const { node, index, to, height } = drag
    const rest = (to - index) * height
    const velocity = drag.velocity
    this.stopLayerTracking()

    const commit = () => {
      this.layerSpring = null
      this.resetLayerRows(drag)
      // `to` is a slot in the list as it stands; moveLayer wants the gap the
      // row lands in, which is one further along when it travelled downwards.
      landed()
      this.moveLayer(index, to > index ? to + 1 : to)
      // The click that follows this pointerup would otherwise re-select.
      setTimeout(() => { this.layerDragged = false }, 0)
    }

    if (prefersReducedMotion()) {
      commit()
      return
    }

    this.layerSpring = new Spring({
      value: drag.offset,
      velocity,
      response: 0.3,
      damping: 1,
      onUpdate: (value) => { node.style.transform = `translate3d(0, ${value}px, 0)` },
      onRest: commit
    })
    this.layerSpring.to(rest)
  }

  layerPointerCancel() {
    const drag = this.layerDrag
    this.stopLayerTracking()
    if (!drag) return

    this.resetLayerRows(drag)
    this.layerDragged = false
  }

  stopLayerTracking() {
    if (this.layerFrame != null) cancelAnimationFrame(this.layerFrame)
    this.layerFrame = null

    const drag = this.layerDrag
    if (drag) {
      if (drag.node.hasPointerCapture(drag.pointerId)) drag.node.releasePointerCapture(drag.pointerId)
      if (this.onLayerMove) drag.node.removeEventListener("pointermove", this.onLayerMove)
      if (this.onLayerUp) drag.node.removeEventListener("pointerup", this.onLayerUp)
      if (this.onLayerCancel) drag.node.removeEventListener("pointercancel", this.onLayerCancel)
    }

    this.onLayerMove = null
    this.onLayerUp = null
    this.onLayerCancel = null
    this.layerDrag = null
  }

  // Reordering re-renders the list, but a drop that changes nothing doesn't, so
  // the rows have to be put back either way.
  resetLayerRows(drag) {
    drag.rows.forEach((row) => { row.style.transform = "" })

    const { node } = drag
    node.style.position = ""
    node.style.zIndex = ""
    node.style.transition = ""
    node.style.cursor = ""
    node.style.boxShadow = ""
    node.style.transform = ""
    delete node.dataset.dragging
  }

  moveLayer(from, to) {
    // Dropping either side of the row it came from is a no-op.
    const target = to > from ? to - 1 : to
    if (target === from) return

    const stack = [...this.elements].reverse()
    const [element] = stack.splice(from, 1)
    stack.splice(target, 0, element)
    this.doc.elements = stack.reverse()
    this.commit()
  }

  addElement(event) {
    const type = event.target.value
    event.target.value = ""
    if (!type) return

    const element = this.blankElement(type)
    this.elements.push(element)
    this.selectedIds = [element.id]
    this.commit()
  }

  newId() {
    return `el_${Math.random().toString(36).slice(2, 8)}`
  }

  blankElement(type) {
    const base = {
      id: this.newId(),
      type,
      name: type.charAt(0).toUpperCase() + type.slice(1),
      x: 60, y: 60, w: 200, h: 60,
      rotation: 0, hidden: false, locked: false
    }

    switch (type) {
      case "text":
        return {
          ...base, content: "New text", font: this.fontsValue[0] ? this.fontsValue[0][1] : "castoro",
          size: 14, line_height: 1.3, letter_spacing: 0, color: "#000000",
          align: "center", valign: "middle", font_weight: "normal", font_style: "normal",
          text_transform: "none", fit: "none", min_size: 8, clip: false
        }
      case "image":
        return {
          ...base, source: { kind: "binding", key: Object.keys(this.bindingsValue.images)[0] },
          object_fit: "contain", lock_aspect: true
        }
      case "box":
        return { ...base, fill: "transparent", border_color: "#000000", border_width: 1, radius: 0 }
      case "qr":
        return {
          ...base, name: "QR code", w: 80, h: 80, content: "{{certificate_url}}",
          color: "#000000", fill: "#ffffff", margin: 4, lock_aspect: true
        }
      default:
        return { ...base, h: 2, color: "#000000" }
    }
  }

  deleteSelected() {
    const ids = this.selectedIds
    if (ids.length === 0) return

    this.doc.elements = this.elements.filter((candidate) => !ids.includes(candidate.id))
    this.selectedIds = []
    this.commit()
  }

  // Each copy lands just above its original, nudged so both stay visible, and
  // the copies become the selection so the block can be dragged into place.
  duplicateSelected() {
    const originals = this.selection
    if (originals.length === 0) return

    const copies = originals.map((el) => {
      const copy = JSON.parse(JSON.stringify(el))
      copy.id = this.newId()
      // "Badge copy" duplicated again stays "Badge copy", not "Badge copy copy".
      const name = el.name || el.type
      copy.name = name.endsWith(" copy") ? name : `${name} copy`
      copy.x = this.round(this.num(el.x) + DUPLICATE_OFFSET)
      copy.y = this.round(this.num(el.y) + DUPLICATE_OFFSET)
      return copy
    })

    copies.forEach((copy, i) => this.elements.splice(this.elements.indexOf(originals[i]) + 1, 0, copy))
    this.selectedIds = copies.map((copy) => copy.id)
    this.commit()
  }

  select(ids) {
    const wanted = ids === null || ids === undefined ? [] : [].concat(ids)
    this.selectedIds = wanted.filter((id) => this.elements.some((el) => el.id === id))
    this.refreshPanels()
  }

  toggleSelected(id) {
    this.select(this.isSelected(id) ? this.selectedIds.filter((other) => other !== id) : [...this.selectedIds, id])
  }

  // Everything that reflects the document apart from the frame itself.
  refreshPanels() {
    this.renderLayers()
    this.renderOverlay()
    this.fillInspector()
  }

  // --- inspector -----------------------------------------------------------

  // Every <select data-options="…"> fills itself from the server's lists,
  // however many groups share one — text, line and QR layers all pick colours.
  populatePickers() {
    const option = (value, label) => `<option value="${value}">${label}</option>`
    const tagged = (entries) => Object.entries(entries).map(([key, label]) => option(`{{${key}}}`, label)).join("")
    const colours = option("custom", "Custom colour") + tagged(this.bindingsValue.colors)

    const conditional = { ...this.bindingsValue.text, ...this.bindingsValue.images }
    const when = (state) => Object.entries(conditional).map(([key, label]) => option(`${state}:${key}`, label)).join("")

    const lists = {
      fonts: this.fontsValue.map(([label, key]) => option(key, label)).join(""),
      tags: option("", "Insert data…") + tagged(this.bindingsValue.text),
      colours,
      fills: option("transparent", "None") + colours,
      conditions: option("", "Always") +
        `<optgroup label="Only when there is a…">${when("present")}</optgroup>` +
        `<optgroup label="Only when there is no…">${when("absent")}</optgroup>`
    }

    this.inspectorTarget.querySelectorAll("[data-options]").forEach((select) => {
      select.innerHTML = lists[select.dataset.options] || ""
    })
  }

  renderSourceOptions() {
    const bindings = Object.entries(this.bindingsValue.images)
      .map(([key, label]) => `<option value="binding:${key}">${label}</option>`)
      .join("")
    const uploads = this.images
      .map((image) => `<option value="upload:${image.signed_id}">${image.filename}</option>`)
      .join("")

    this.inspectorTarget.querySelector('[data-field="source"]').innerHTML =
      `<optgroup label="Certificate data">${bindings}</optgroup>` +
      (uploads ? `<optgroup label="Uploads">${uploads}</optgroup>` : "")
  }

  // Several groups carry a field of the same name, so a field's partner — the
  // swatch behind a colour picker, the textarea a tag drops into — is found
  // inside the group the input sits in, never the first one on the page.
  sibling(input, field) {
    return input.closest("[data-group]").querySelector(`[data-field="${field}"]`)
  }

  fillInspector() {
    const count = this.selectedIds.length
    const el = this.selected

    this.inspectorTarget.classList.toggle("hidden", count === 0)
    this.emptyTarget.classList.toggle("hidden", count > 0)
    if (count === 0) return

    this.inspectorTarget.querySelectorAll("[data-group]").forEach((group) => {
      const name = group.dataset.group
      let show
      if (name === "arrange") show = true
      else if (name === "multi") show = count > 1
      else show = count === 1 && (name === "common" || name === el.type)
      group.classList.toggle("hidden", !show)
    })

    if (this.hasCountTarget) this.countTarget.textContent = `${count} layers selected`
    this.inspectorTarget.querySelectorAll("[data-axis]").forEach((button) => { button.disabled = count < 3 })

    if (el) this.visibleFields().forEach((input) => this.readInto(input, el))
  }

  visibleFields() {
    return [...this.inspectorTarget.querySelectorAll("[data-field]")].filter(
      (input) => !input.closest("[data-group]").classList.contains("hidden")
    )
  }

  readInto(input, el) {
    const field = input.dataset.field

    if (field === "source") {
      const source = el.source || {}
      input.value = source.kind === "upload" ? `upload:${source.signed_id}` : `binding:${source.key}`
      return
    }

    if (field === "condition") {
      const condition = el.condition || {}
      input.value = condition.key ? `${condition.when === "absent" ? "absent" : "present"}:${condition.key}` : ""
      return
    }

    if (field.endsWith("_swatch")) {
      const value = el[field.replace("_swatch", "")]
      input.value = /^#[0-9a-f]{6}$/i.test(value || "") ? value : "#000000"
      return
    }

    if (this.isColourField(field)) {
      const value = el[field]
      const custom = !String(value || "").startsWith("{{")
      input.value = custom ? (value === "transparent" ? "transparent" : "custom") : value
      return
    }

    if (input.type === "checkbox") {
      input.checked = field === "lock_aspect" ? this.aspectLocked(el) : Boolean(el[field])
      return
    }

    input.value = el[field] === undefined || el[field] === null ? "" : el[field]
  }

  isColourField(field) {
    return ["color", "fill", "border_color"].includes(field)
  }

  fieldChanged(event) {
    const input = event.target
    const field = input.dataset.field
    const el = this.selected
    if (!field || !el) return

    if (field === "source") {
      const [kind, value] = input.value.split(":")
      el.source = kind === "upload" ? { kind: "upload", signed_id: value } : { kind: "binding", key: value }
    } else if (field === "condition") {
      const [when, key] = input.value.split(":")
      if (key) el.condition = { key, when }
      else delete el.condition
    } else if (field.endsWith("_swatch")) {
      const base = field.replace("_swatch", "")
      el[base] = input.value
      const picker = this.sibling(input, base)
      if (picker) picker.value = "custom"
    } else if (this.isColourField(field)) {
      el[field] = input.value === "custom" ? this.swatchValue(input, field) : input.value
    } else if (input.type === "checkbox") {
      el[field] = input.checked
    } else if (input.type === "number") {
      el[field] = input.value === "" ? null : parseFloat(input.value)
    } else {
      el[field] = input.value
    }

    // Geometry can be applied to the live frame; anything else changes the
    // markup, so let the server re-render.
    if (GEOMETRY_FIELDS.includes(field)) {
      this.syncFrameElement(el)
      this.refit()
    }

    // Only the panels that show the field get redrawn: the layer list carries
    // the name and visibility, the overlay fades hidden layers and changes the
    // cursor on locked ones.
    if (field === "name" || field === "hidden") this.renderLayers()
    if (GEOMETRY_FIELDS.includes(field) || field === "hidden" || field === "locked") this.renderOverlay()

    this.pushHistory(`field:${el.id}:${field}`)
    this.scheduleSave({ refresh: !LOCAL_FIELDS.includes(field) })
  }

  swatchValue(input, field) {
    const swatch = this.sibling(input, `${field}_swatch`)
    return swatch ? swatch.value : "#000000"
  }

  insertTag(event) {
    const tag = event.target.value
    event.target.value = ""
    const el = this.selected
    if (!tag || !el) return

    const textarea = this.sibling(event.target, "content")
    const at = textarea.selectionStart ?? textarea.value.length
    textarea.value = `${textarea.value.slice(0, at)}${tag}${textarea.value.slice(at)}`
    el.content = textarea.value

    this.pushHistory()
    this.scheduleSave()
  }

  // --- images --------------------------------------------------------------

  async loadImages() {
    const response = await fetch(this.imagesUrlValue, { headers: { Accept: "application/json" } })
    if (!response.ok) return

    const data = await response.json()
    this.images = data.images || []
    this.renderSourceOptions()
    this.fillInspector()
  }

  async uploadImage(event) {
    const file = event.target.files[0]
    if (!file) return

    const body = new FormData()
    body.append("file", file)

    this.statusTarget.textContent = "Uploading…"
    const response = await fetch(this.imagesUrlValue, {
      method: "POST",
      headers: { Accept: "application/json", "X-CSRF-Token": this.csrfToken },
      body
    })
    event.target.value = ""

    if (!response.ok) {
      this.statusTarget.textContent = "Upload failed"
      return
    }

    const image = await response.json()
    this.images.push(image)
    this.renderSourceOptions()

    const el = this.selected
    if (el && el.type === "image") {
      el.source = { kind: "upload", signed_id: image.signed_id }
      this.fillInspector()
      this.pushHistory()
      this.scheduleSave()
    } else {
      this.statusTarget.textContent = "Uploaded"
    }
  }

  // --- history and saving --------------------------------------------------

  // A burst of small edits from one continuous action — a held arrow key, a
  // name typed a letter at a time — is one thing the admin did, so it gets one
  // undo entry. Without this a two-second key repeat pushes fifty snapshots and
  // the real history falls off the end of the stack.
  pushHistory(coalesceKey = null) {
    const snapshot = JSON.stringify(this.doc)

    if (coalesceKey && this.historyCoalesceKey === coalesceKey && this.past.length > 1) {
      this.past[this.past.length - 1] = snapshot
    } else {
      this.past.push(snapshot)
      if (this.past.length > HISTORY_LIMIT) this.past.shift()
    }

    this.future = []
    this.historyCoalesceKey = coalesceKey

    clearTimeout(this.coalesceTimer)
    if (coalesceKey) {
      this.coalesceTimer = setTimeout(() => { this.historyCoalesceKey = null }, HISTORY_COALESCE_MS)
    }
  }

  // The snapshot stack holds the state *before* each change, so stepping back
  // means swapping the current document onto the redo pile.
  undo() {
    if (this.past.length < 2) return

    this.future.push(this.past.pop())
    this.doc = this.normalise(JSON.parse(this.past[this.past.length - 1]))
    this.afterHistoryJump()
  }

  redo() {
    const next = this.future.pop()
    if (!next) return

    this.past.push(next)
    this.doc = this.normalise(JSON.parse(next))
    this.afterHistoryJump()
  }

  afterHistoryJump() {
    this.selectedIds = this.selectedIds.filter((id) => this.elements.some((el) => el.id === id))
    this.refreshPanels()
    this.save({ refresh: true })
  }

  // A change worth persisting: snapshot, redraw the panels, re-render.
  commit() {
    this.pushHistory()
    this.refreshPanels()
    this.scheduleSave()
  }

  // `refresh` asks the server for fresh HTML with the save. Geometry never
  // needs it — the frame was updated by hand as it happened.
  scheduleSave({ refresh = true } = {}) {
    this.pendingRefresh = this.pendingRefresh || refresh
    clearTimeout(this.saveTimer)
    this.saveTimer = setTimeout(() => this.save(), SAVE_DELAY)
  }

  // One PATCH in flight at a time. Two overlapping saves race on lock_version —
  // the second carries the version the first is about to bump and comes back
  // as a conflict — and their HTML can land out of order. A change made
  // mid-flight waits its turn and goes out as soon as the current save returns.
  async save({ refresh = false, keepalive = false } = {}) {
    clearTimeout(this.saveTimer)
    this.saveTimer = null
    this.pendingRefresh = this.pendingRefresh || refresh

    if (this.saving) {
      this.saveQueued = true
      return
    }

    this.saving = true
    try {
      do {
        this.saveQueued = false
        const wantsHtml = this.pendingRefresh
        this.pendingRefresh = false
        await this.performSave({ wantsHtml, keepalive })
      } while (this.saveQueued)
    } finally {
      this.saving = false
    }
  }

  async performSave({ wantsHtml, keepalive }) {
    this.statusTarget.textContent = "Saving…"

    const body = new FormData()
    body.append("certificate_design[document]", JSON.stringify(this.doc))
    body.append("certificate_design[lock_version]", this.lockVersionValue)
    if (wantsHtml) body.append("render", "html")
    Object.entries(this.previewParams()).forEach(([key, value]) => body.append(key, value))

    let response
    try {
      response = await fetch(this.updateUrlValue, {
        method: "PATCH",
        headers: { Accept: "application/json", "X-CSRF-Token": this.csrfToken },
        body,
        keepalive
      })
    } catch (_error) {
      this.statusTarget.textContent = "Offline — changes not saved"
      return
    }

    if (response.status === 409) {
      this.statusTarget.textContent = "Edited in another tab — reload to continue"
      return
    }

    if (!response.ok) {
      this.statusTarget.textContent = "Could not save"
      return
    }

    const data = await response.json()
    this.lockVersionValue = data.lock_version
    this.statusTarget.textContent = `Saved ${new Date(data.saved_at).toLocaleTimeString()}`

    if (data.html) this.setFrameHtml(data.html)
  }

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }

  // --- keyboard ------------------------------------------------------------

  keydown(event) {
    if (this.isTyping(event.target)) return

    const command = event.metaKey || event.ctrlKey
    const key = event.key.toLowerCase()

    if (command && key === "z") {
      event.preventDefault()
      event.shiftKey ? this.redo() : this.undo()
      return
    }

    if (command && key === "a") {
      event.preventDefault()
      this.select(this.elements.map((el) => el.id))
      return
    }

    if (command && key === "d") {
      if (this.selectedIds.length === 0) return
      event.preventDefault()
      this.duplicateSelected()
      return
    }

    if (event.key === "Escape") {
      // Escape belongs to whatever is in flight before it reaches selection.
      if (this.layerDrag) {
        this.layerPointerCancel()
        return
      }
      if (this.drag || this.marquee) {
        this.pointerCancel()
        return
      }

      this.select([])
      return
    }

    const items = this.movable
    if (items.length === 0) return

    if (event.key === "Backspace" || event.key === "Delete") {
      event.preventDefault()
      const ids = items.map((el) => el.id)
      this.doc.elements = this.elements.filter((candidate) => !ids.includes(candidate.id))
      this.selectedIds = []
      this.commit()
      return
    }

    const step = event.shiftKey ? 10 : 1
    const nudges = { ArrowLeft: ["x", -step], ArrowRight: ["x", step], ArrowUp: ["y", -step], ArrowDown: ["y", step] }
    const nudge = nudges[event.key]
    if (!nudge) return

    event.preventDefault()
    items.forEach((el) => {
      el[nudge[0]] = this.round(this.num(el[nudge[0]]) + nudge[1])
      this.syncFrameElement(el)
    })
    this.renderOverlay()
    this.fillInspector()
    this.pushHistory(`nudge:${this.selectedIds.join(",")}`)
    this.scheduleSave({ refresh: false })
  }

  isTyping(node) {
    return node && (node.tagName === "INPUT" || node.tagName === "TEXTAREA" || node.tagName === "SELECT")
  }

  // --- helpers -------------------------------------------------------------

  num(value) {
    const number = parseFloat(value)
    return Number.isFinite(number) ? number : 0
  }

  round(value) {
    return Math.round(value * 100) / 100
  }
}
